import { InMemoryCache, Reference } from '@apollo/client';
import { getAxisConfig } from '@thoughtspot/chart-lib/lib/core/chart-model-utils';
import {
    applyVisualOverrides,
    initClientState,
    isClientStateUpgradeRequired,
    reuseClientState,
    upgradeClientStateForBlinkV2,
    VizPropModel,
} from '@thoughtspot/client-state-util';
import { recoverConditionalFormattingForColumns } from '@thoughtspot/client-state-util/esm/conditional-formatting-upgrade-util';
import { create } from '@thoughtspot/logger';
import { isChartSkillsEnabled } from '@thoughtspot/session-service';
import _ from 'lodash';
import {
    Answer,
    AnswerColumn,
    AnswerColumnProps,
    AnswerMetadata,
    ChartConfig,
    ChartType,
    ChartVisualOverrides,
    ChartViz,
    ChartVizProps,
    ClientState,
    HeadlineVisualOverrides,
    HeadlineViz,
    HeadlineVizProps,
    TableVisualOverrides,
    TableViz,
    TableVizProps,
    VizColumn,
} from '/@services/generated/graphql-types';
import { readFromLBChartVizCache } from './lb-chartViz-caching-service';
import {
    readFromVizPropCache,
    writeToVizPropCache,
} from './vizprop-caching-service';

const logger = create('v2-client-state-utils');

// making sure entry point to clientStateUtil
// is only isClientStateUpgradeRequired: to check if upgrade is needed
// initClientState to initialize for new answer
// upgradeClientStateForBlinkV2 to upgraded the viz by typename
export const upgradeVizModel = (
    typeName: 'HeadlineViz' | 'ChartViz' | 'TableViz',
    clientState: Record<string, any> = {},
    vizProps: any,
    columns: VizColumn[] = [],
    chartConfig?: ChartConfig,
    shouldInit?: boolean,
    metadata?: AnswerMetadata,
    visualOverrides?:
        | ChartVisualOverrides
        | TableVisualOverrides
        | HeadlineVisualOverrides
        | null,
) => {
    let vizDetails: VizPropModel = {
        typeName,
        clientState: _.cloneDeep(clientState),
        vizProps: _.cloneDeep(vizProps),
        isNewAnswer: metadata?.generationNum === '-1',
        columns: columns.map(col => {
            return {
                id: col.column.id,
                metricDefinition: col.legacyMetricDefinition as any,
            };
        }),
        visualOverrides,
    } as VizPropModel;

    if (vizDetails.typeName === 'ChartViz') {
        // todo - it's temporary fix, need to find better way to access chartViz
        const axisConfig = getAxisConfig({
            config: chartConfig,
        } as ChartViz);
        const chartType = chartConfig.chartType as ChartType;
        vizDetails = {
            ...vizDetails,
            axisConfig: {
                xAxisColumns: axisConfig.x?.map(col => col?.id),
                yAxisColumns: axisConfig.y?.map(col => col?.id),
                categoryColumns: axisConfig.category[0]?.id,
                legendColumns: axisConfig.color.map(col => col?.id),
                radialColumn: axisConfig.size?.id,
            },
            chartType,
        };
    }

    /*
        The following function will only init the chart if chart upgrade is required.
        It doesnt today check if the chart is initialized or not.
        If chart is already initialized but in older client state and function expects to be initialized,
        it will dump the existing props and recreate a entire viz props

        The shouldInit will only kick in if the viz prop is in older version, else it serves no purpose today.
        Given we upgrade many objects already on upgrader service, this is redundant code and should be removed.

        The main work of adjusting the client state is done inside reuseClientState function,
        which cleans up the existing viz props as per the new configuration.
    */
    let newClientState = vizProps;
    if (isClientStateUpgradeRequired(vizDetails)) {
        const fn = shouldInit ? initClientState : upgradeClientStateForBlinkV2;
        try {
            newClientState = fn(vizDetails);
        } catch (e) {
            logger.warn(e);
        }
    }

    // Check for updating axisProperties and columnProperties
    // on changing config or adding or removing columns
    newClientState = reuseClientState({
        ...vizDetails,
        vizProps: _.cloneDeep(newClientState),
    });

    if (
        vizDetails.typeName === 'ChartViz' ||
        vizDetails.typeName === 'TableViz'
    ) {
        newClientState = recoverConditionalFormattingForColumns(newClientState);
    }

    if (isChartSkillsEnabled()) {
        newClientState = applyVisualOverrides(
            {
                ...vizDetails,
                vizProps: _.cloneDeep(newClientState),
            },
            columns,
        );
    }

    return newClientState;
};

export const fetchVizPropertiesFromCache = (
    viz: ChartViz | TableViz | HeadlineViz,
    readField: any,
    sessionId: string,
    genNo: number,
): {
    typeName: 'HeadlineViz' | 'ChartViz' | 'TableViz';
    clientState: ClientState;
    vizProps: ChartVizProps | TableVizProps | HeadlineVizProps;
    columns: VizColumn[];
    chartConfig: ChartConfig;
    vizId: string;
    visualOverrides:
        | ChartVisualOverrides
        | TableVisualOverrides
        | HeadlineVisualOverrides;
} => {
    const typeName = readField('__typename', viz);
    let clientState;
    let vizProps;
    let columns;
    let chartConfig;
    let vizId;
    let visualOverrides;
    const chartVizFromCache = readFromLBChartVizCache(sessionId);
    if (
        typeName === 'ChartViz' &&
        chartVizFromCache?.genNo &&
        chartVizFromCache?.genNo < genNo
    ) {
        clientState = _.cloneDeep(readField('clientState', viz) || {});
        vizProps = readField('vizProp', viz);
        columns = (readField('columns', viz) as Array<VizColumn>) || [];
        chartConfig = readField('config', viz) as ChartConfig;
        vizId = readField('id', viz);
        visualOverrides = readField('visualOverrides', viz);
    }
    if (typeName === 'ChartViz' && !!chartVizFromCache?.chartViz) {
        const chartViz = chartVizFromCache?.chartViz;
        clientState = _.isEmpty(clientState)
            ? _.cloneDeep(chartViz.clientState || {})
            : clientState;
        vizProps = _.isEmpty(vizProps) ? chartViz.vizProp : vizProps;
        columns = _.isEmpty(columns) ? chartViz.columns || [] : columns;
        chartConfig = chartConfig
            ? { ...chartViz.config, ...chartConfig }
            : chartViz.config;
        vizId = vizId ?? chartViz.id;
        visualOverrides = visualOverrides ?? chartViz.visualOverrides;
    } else {
        clientState = _.cloneDeep(readField('clientState', viz) || {});
        vizProps = readField('vizProp', viz);
        columns = (readField('columns', viz) as Array<VizColumn>) || [];
        chartConfig = readField('config', viz) as ChartConfig;
        vizId = readField('id', viz);
        visualOverrides = readField('visualOverrides', viz);
    }
    return {
        typeName,
        clientState,
        vizProps,
        columns,
        chartConfig,
        vizId,
        visualOverrides,
    };
};

// TODO(chetan): move this file to the service layer.
export const upgradeOrInitVizClientState = (
    shouldInit: boolean,
    isPinboardContextBook = false,
) => (existing: Reference, incoming: Reference, props: any) => {
    const { readField, cache, variables } = props;
    const answerRef = isPinboardContextBook
        ? readField('answerEditSession', readField('contextBook', incoming))
        : incoming;

    if (!(variables?.isClientStateUpgradeRequired === undefined)) {
        return incoming;
    }
    const genNo = readField('genNo', readField('id', answerRef));
    const sessionId = readField('sessionId', readField('id', answerRef));
    const answer: Answer = readField('answer', answerRef);
    const answerId = readField('id', answer);
    const metadata = readField('metadata', answer);
    const visualizations: any = readField('visualizations', answer);

    _.forEach(visualizations, viz => {
        const {
            typeName,
            clientState,
            vizProps: existingVizProps,
            columns,
            chartConfig,
            vizId,
            visualOverrides,
        } = fetchVizPropertiesFromCache(viz, readField, sessionId, genNo);
        let vizProps = existingVizProps;
        if (vizProps === undefined) {
            // empty visualisation is coming in graphql so ignoring the upgrade for those.
            return;
        }
        if (
            !_.isNil(variables?.clientState) &&
            !_.isNil(variables?.vizId) &&
            variables?.vizId === vizId
        ) {
            vizProps = variables?.clientState;
        }

        // Check if incoming vizProps from API response is valid (has version field).
        // A valid vizProps indicates the API returned fresh data that should be used.
        // Note: chart/headline use `version`, table uses `tableVizPropVersion`.
        const hasValidIncomingVizProps =
            !_.isNil(vizProps) &&
            (!_.isNil((vizProps as any)?.version) ||
                !_.isNil((vizProps as any)?.tableVizPropVersion));

        // Look up the local vizPropCache for previously stored vizProps at this genNo
        let newClientState = readFromVizPropCache(
            answerId,
            vizId,
            sessionId,
            genNo,
        );
        let cacheHit = false;

        // Decide whether to use API data or cached data:
        //
        // | Cache State   | API vizProps Valid | Action                    |
        // |---------------|-------------------|---------------------------|
        // | Empty (miss)  | No                | Use API data (upgradeVizModel) |
        // | Empty (miss)  | Yes               | Use API data (upgradeVizModel) |
        // | Exists (hit)  | Yes               | Use API data (upgradeVizModel) |
        // | Exists (hit)  | No                | Use cached data           |
        //
        // Key insight: When LoadExistingAnswerSession returns valid vizProps from
        // the server (e.g., during reset/undo), we must use that fresh data instead
        // of the cached value, which may contain stale user modifications.
        // The cache is only used as a fallback when the API doesn't return valid
        // vizProps (e.g., for operations that don't fetch full vizProp data).
        if (_.isNil(newClientState) || hasValidIncomingVizProps) {
            newClientState = upgradeVizModel(
                typeName,
                clientState,
                vizProps,
                columns,
                chartConfig,
                shouldInit,
                metadata,
                visualOverrides,
            );
        } else {
            cacheHit = true;
        }

        try {
            cache.modify({
                // eslint-disable-next-line no-underscore-dangle
                id: viz.__ref,
                fields: {
                    vizProp() {
                        return newClientState;
                    },
                },
            });
        } catch (error) {
            if (!error?.message?.includes('already recomputing')) {
                throw error;
            }
        }
        if (!cacheHit) {
            writeToVizPropCache(
                answerId,
                vizId,
                sessionId,
                genNo,
                newClientState,
            );
        }
    });

    return incoming;
};

export const upgradeAnswerColumnProps = (
    column: AnswerColumn,
): AnswerColumn => {
    let columnProps = column.columnProps;
    const vizModel = {
        typeName: 'AnswerColumn',
        answerSheetClientState: column.legacySheetProperties,
        columnId: column.id,
        vizProps: columnProps,
        legacyColumnFormatProperties: column.legacyColumnFormatProperties,
    } as VizPropModel;
    if (isClientStateUpgradeRequired(vizModel)) {
        columnProps = upgradeClientStateForBlinkV2(
            vizModel,
        ) as AnswerColumnProps;
    }

    return {
        ...column,
        columnProps,
    };
};

export const upgradeOrInitMultipleVizClientState = (shouldInit: boolean) => (
    existing: Reference,
    incomingAnswers: Reference,
    params: { readField: any; cache: InMemoryCache },
) => {
    if (Array.isArray(incomingAnswers)) {
        return incomingAnswers.map(incomingAnswer => {
            return upgradeOrInitVizClientState(shouldInit)(
                existing,
                incomingAnswer,
                params,
            );
        });
    }
    return incomingAnswers;
};
